import { ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { Subject, map, takeUntil } from 'rxjs';

import { Router } from '@angular/router';
import * as _ from 'lodash';
import { BladeService } from 'src/app/modules/blades/services/blade.service';
import { VehicleStore } from 'src/app/modules/core/store';
import { LineStore } from 'src/app/modules/core/store/line.store';
import { ColumnFactory } from 'src/app/modules/shared/helpers/column-factory';
import { CompactListColumn, CompactListItem } from 'src/app/modules/shared/models';
import { Vehicle } from 'src/app/modules/shared/models/vehicle';
import { SpinnerComponent } from '../../../../modules/shared/components/spinner/spinner.component';
import { TranslateModule } from '@ngx-translate/core';
import { FeedbackTextComponent } from '../../../../modules/shared/components/feedback-text/feedback-text.component';
import { CompactListComponent } from '../../../../modules/shared/components/compact-list/compact-list.component';


@Component({
    selector: 'app-line-selection-overview',
    templateUrl: './line-selection-overview.component.html',
    styleUrls: ['./line-selection-overview.component.scss'],
    imports: [CompactListComponent, FeedbackTextComponent, TranslateModule, SpinnerComponent],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class LineSelectionOverviewComponent implements OnInit, OnDestroy {
    private _lineStore = inject(LineStore);
    private _vehicleStore = inject(VehicleStore);
    private _router = inject(Router);
    private _bladeService = inject(BladeService);

    @Output() nrItems = new EventEmitter<number>();

    public items: CompactListItem[];
    public columns: CompactListColumn[];

    private _stateKey = 'line-selection-overview-component-state';
    private _ngUnsubscribe$ = new Subject<void>();
    private _groupedVehicles: _.Dictionary<Vehicle[]>;
    private _selectedLineId: string;


    constructor() {
        this._initState();
    }

    ngOnInit() {
        this._lineStore.getLinesState().pipe(
            map(lines => {
                if (!lines) {
                    // needed for the spinner to show instead of empty data-feedback
                    return null;
                } else {
                    return _.map(lines, line => {
                        const item = new CompactListItem();
                        item.id = line.id;
                        item.type = 'line';
                        item.selected = this._selectedLineId === item.id;
                        item.properties['routeNumber'] = line.routeNumber;
                        item.properties['trafficAreaName'] = line.trafficAreaName;
                        item.properties['vehicleList'] = this._groupedVehicles ? this._groupedVehicles[line.id] : [];
                        return item;
                    });
                }
            }),
            takeUntil(this._ngUnsubscribe$)
        ).subscribe((items) => {
            this.items = items;
            this.nrItems.next(this.items?.length);
        });

        this._vehicleStore.getVehiclesState()
            .pipe(takeUntil(this._ngUnsubscribe$))
            .subscribe(vehicles => {
                this._groupedVehicles = _.groupBy(vehicles, vehicle => vehicle.lineId);

                _.each(this._groupedVehicles, (vehicleGroup, key) => {
                    this._groupedVehicles[key] =
                        _.sortBy(vehicleGroup, vehicle => -vehicle.departureDeviation?.deviation.getTotalSeconds());
                });

                this.connectVehiclesToList();
            });

        // to set selection on the line
        this._bladeService.allOutletParams$.pipe(
            takeUntil(this._ngUnsubscribe$)
        ).subscribe(params => {
            if (!params) return;
            const lineId = params['lineId'];
            this._selectedLineId = lineId;

            this._resetSelection();

            if (lineId) {
                this._selectItem(lineId);
            }
        });
    }

    ngOnDestroy() {
        this._ngUnsubscribe$.next();
        this._ngUnsubscribe$.complete();
    }

    public connectVehiclesToList() {
        _.forEach(this.items, item => {
            item.properties['vehicleList'] = this._groupedVehicles ? this._groupedVehicles[item.id] : [];
        });

    }


    public itemSelected(item: CompactListItem) {
        const encodedItemId = encodeURIComponent(item.id);
        const indexOfItemId = this._router.routerState.snapshot.url.indexOf(`outlet-right0:line/${encodedItemId}`);
        if (indexOfItemId === -1) {
            this._router.navigate(['', { outlets: { 'outlet-right0': ['line', item.id], 'outlet-right1': null, 'outlet-right2': null }}]);
        }
    }

    public columnsUpdated(columns: CompactListColumn[]) {
        this._updateState();
    }

    private _selectItem(id: string) {
        if (!this.items) {
            return;
        }

        const found = _.find(this.items, item => item.id === id);
        if (found) {
            found.selected = true;
        }
    }

    private _resetSelection() {
        // reset selection
        const selectedItems = _.chain(this.items)
            .filter(item => item.selected)
            .value();
        _.each(selectedItems, item => item.selected = false);
    }

    private _initState() {
        this._initColumnDefaults();

        const stateData = localStorage.getItem(this._stateKey);
        if (stateData) {
            const state = Object.assign(new LineSelectionOverviewComponentState(), JSON.parse(stateData));
            this.columns = _.map(this.columns, (column) => {
                const storedColumn = _.find(state.columns, (c) => c.key === column.key);
                if (storedColumn) {
                    delete column.sort;
                    delete storedColumn.width;
                    delete storedColumn.sortNumeric;
                    delete storedColumn.icon;
                }
                return { ...column, ...storedColumn };
            });
        }
    }

    private _updateState() {
        let state = new LineSelectionOverviewComponentState();
        state = {
            ...state,
            columns: this.columns
        };
        localStorage.setItem(this._stateKey, JSON.stringify(state));
    }

    private _initColumnDefaults() {
        this.columns = [
            ColumnFactory.getRouteNumberColumn({ sort: 'asc' }),
            ColumnFactory.getTrafficAreaNameColumn(),
            ColumnFactory.getVehicleListColumn()
        ];
    }

}

export class LineSelectionOverviewComponentState {
    public columns: CompactListColumn[];
}
